import React, { useState, useEffect } from 'react';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { reverseGeocode, distanceBetweenGeoInfo } from '../lib/geolocation';
import { describeGeoInfo } from '../lib/geoInfo';

// Componente compacto: muestra "Calle X, Ciudad" (cache) y expande lat/lng al click.
// Si recibe `workplace` (con geoCoordinates + maxGeoDeviation), compara la
// distancia del fichaje al centro de la sede y, si supera el radio permitido,
// pinta el icono y la dirección en rojo con el texto "(fuera de la sede, X m)".
// Reutilizado en el detalle del trabajador (Equipo) y en Aprobaciones.
//
// Fichajes manuales y correcciones (geoInfo con origen=manual|correccion):
// la posición es la del momento de REGISTRARLOS, no la del fichaje, así que
// se presenta como "Registrado desde: …" con una etiqueta "Manual" /
// "Corrección", y la distancia a la sede es informativa (sin rojo).
// Sin coordenadas: "Sin ubicación" (o "Manual · sin ubicación").
const LocationCell = ({ geoInfo, workplace }) => {
  const [label, setLabel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const info = describeGeoInfo(geoInfo);
  const hasCoords = info.hasCoords;

  useEffect(() => {
    let cancelled = false;
    if (!hasCoords) return undefined;
    setLoading(true);
    reverseGeocode(geoInfo).then((l) => { if (!cancelled) { setLabel(l); setLoading(false); } });
    return () => { cancelled = true; };
  }, [geoInfo, hasCoords]);

  if (!hasCoords) {
    return <span className="text-ink-muted/60 italic text-xs">{info.missingLabel}</span>;
  }

  // Distancia y "fuera de sede". Solo si la sede tiene coordenadas.
  const wpCoords = workplace?.geoCoordinates || null;
  const distance = wpCoords ? distanceBetweenGeoInfo(geoInfo, wpCoords) : null;
  const radius = Number(workplace?.maxGeoDeviation) > 0 ? Number(workplace.maxGeoDeviation) : 100;
  const outOfSite = !info.isManual && distance != null && distance > radius;
  const iconColor = outOfSite ? 'text-danger' : 'text-primary';
  const labelColor = outOfSite ? 'text-danger hover:text-danger' : 'text-ink hover:text-primary';

  return (
    <div className="flex items-start gap-1.5">
      <MapPin size={11} className={`${iconColor} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {info.tag && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-ink-muted text-[10px] font-bold uppercase tracking-wider"
              title="Ubicación del dispositivo en el momento de registrar el fichaje (no la del fichaje)"
            >
              {info.tag}
            </span>
          )}
          {info.prefix && <span className="text-[11px] text-ink-muted">{info.prefix}:</span>}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={`text-left text-xs inline-flex items-center gap-1 ${labelColor}`}
            title="Ver coordenadas"
          >
            <span className="truncate max-w-[200px]">
              {loading ? 'Cargando…' : (label || 'Ubicación desconocida')}
            </span>
            {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
        </div>
        {outOfSite && (
          <div className="text-[10px] font-semibold text-danger">
            (fuera de la sede, {Math.round(distance)} m)
          </div>
        )}
        {info.isManual && distance != null && (
          <div className="text-[10px] text-ink-muted">
            (a {Math.round(distance)} m de la sede)
          </div>
        )}
        {expanded && (
          <div className="mt-0.5 text-[10px] text-ink-muted tabular-nums">
            <a
              href={`https://www.google.com/maps?q=${info.lat},${info.lng}`}
              target="_blank" rel="noopener noreferrer"
              className="hover:text-primary underline"
            >
              {info.lat.toFixed(5)}, {info.lng.toFixed(5)}
            </a>
            {info.accuracy != null && <span className="ml-1">(±{Math.round(info.accuracy)} m)</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationCell;
